import * as React from "react";
import type { ColumnDef } from "@tanstack/react-table";

import { Button, CardHeader, DataTable, EmptyState, ErrorState, Pagination } from "Components/ui";
import { IndianRupee, RefreshCw, Undo2, Wallet } from "Components/ui/icons";
import { PaymentListItem } from "api/billing";
import { getApiErrorMessage } from "api/apiError";
import { cn } from "utils/cn";
import { formatDate, formatDateTime, formatINR, humanize } from "utils/format";
import ConfirmDialog from "Routes/Billing/Components/ConfirmDialog";
import {
  NeedsReviewFlag,
  PAYMENT_METHOD_LABELS,
  PaymentStatusChip,
} from "Routes/Billing/Components/StatusChips";
import { usePaymentActions, usePaymentList } from "../data";
import { BillingPermissions } from "../types";

const PAGE_SIZE = 20;

interface PaymentsTabProps {
  tenantId: string;
  permissions: BillingPermissions;
  onOpenInvoice: (invoiceId: string) => void;
  onRecordPayment: () => void;
}

const methodText = (payment: PaymentListItem) => {
  const method = PAYMENT_METHOD_LABELS[payment.method] || humanize(payment.method);
  if (payment.method === "gateway") {
    return (
      [humanize(payment.provider), payment.instrument ? humanize(payment.instrument) : null]
        .filter(Boolean)
        .join(" · ") || method
    );
  }
  return method;
};

const PaymentsTab: React.FC<PaymentsTabProps> = ({
  tenantId,
  permissions,
  onOpenInvoice,
  onRecordPayment,
}) => {
  const [page, setPage] = React.useState<number>(1);
  const [reversing, setReversing] = React.useState<PaymentListItem | null>(null);
  const payments = usePaymentList({ tenantId, page, pageSize: PAGE_SIZE });
  const actions = usePaymentActions();
  const rows = payments.data ? payments.data.data : [];
  const total = payments.data ? payments.data.total : 0;
  const lastPage = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Step back when the current page empties (e.g. after a void or reversal).
  React.useEffect(() => {
    if (payments.data && page > lastPage) {
      setPage(lastPage);
    }
  }, [payments.data, page, lastPage]);

  const canApprove = permissions.canApprove;
  const columns = React.useMemo<ColumnDef<PaymentListItem>[]>(
    () => [
      {
        id: "date",
        header: "Date",
        cell: ({ row }) => (
          <span
            className="whitespace-nowrap text-foreground"
            title={formatDateTime(row.original.paidAt)}
          >
            {formatDate(row.original.paidOn || row.original.paidAt)}
          </span>
        ),
      },
      {
        id: "invoice",
        header: "Invoice",
        cell: ({ row }) => (
          <Button
            variant="link"
            onClick={() => onOpenInvoice(row.original.invoice)}
            className={cn(row.original.invoiceNumber && "font-mono")}
          >
            {row.original.invoiceNumber || "View invoice"}
          </Button>
        ),
      },
      {
        id: "amount",
        header: "Amount",
        meta: { align: "right" },
        cell: ({ row }) => (
          <span className="whitespace-nowrap font-semibold tabular-nums text-foreground">
            {formatINR(row.original.amount)}
          </span>
        ),
      },
      {
        id: "method",
        header: "Method",
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-foreground">{methodText(row.original)}</span>
        ),
      },
      {
        id: "reference",
        header: "Reference",
        cell: ({ row }) => {
          const payment = row.original;
          const reference = payment.reference || payment.providerPaymentId;
          return (
            <div className="py-2">
              <span
                className={cn("break-all text-muted-foreground", reference && "font-mono text-xs")}
              >
                {reference || "—"}
              </span>
              {payment.status === "reversed" && payment.reverseReason && (
                <p className="mt-0.5 text-xs text-subtle-foreground">
                  Reversed: {payment.reverseReason}
                </p>
              )}
            </div>
          );
        },
      },
      {
        id: "status",
        header: "Status",
        cell: ({ row }) => (
          <span className="inline-flex flex-wrap items-center gap-1">
            <PaymentStatusChip status={row.original.status} />
            {row.original.needsReview && <NeedsReviewFlag note={row.original.reviewNote} />}
          </span>
        ),
      },
      {
        id: "actions",
        header: () => <span className="sr-only">Actions</span>,
        meta: { align: "right" },
        cell: ({ row }) =>
          canApprove && row.original.status === "succeeded" ? (
            <Button
              variant="danger-outline"
              size="xs"
              onClick={() => setReversing(row.original)}
              leftIcon={<Undo2 aria-hidden="true" />}
            >
              Reverse
            </Button>
          ) : null,
      },
    ],
    [canApprove, onOpenInvoice]
  );

  const empty = payments.isError ? (
    <ErrorState
      title="Payments didn't load"
      description={getApiErrorMessage(payments.error)}
      action={
        <Button
          variant="secondary"
          size="sm"
          leftIcon={<RefreshCw aria-hidden="true" />}
          onClick={() => payments.refetch()}
        >
          Retry
        </Button>
      }
    />
  ) : (
    <EmptyState
      compact
      icon={<Wallet aria-hidden="true" />}
      title="No payments yet"
      description="Payments made through the pay link show up here within seconds."
    />
  );

  return (
    <section className="space-y-4">
      <CardHeader
        className="p-0"
        title="Payments"
        description="Online payments arrive automatically; record bank transfers, UPI, cheques and cash here."
        actions={
          canApprove ? (
            <Button
              size="sm"
              onClick={onRecordPayment}
              leftIcon={<IndianRupee aria-hidden="true" />}
            >
              Record payment
            </Button>
          ) : null
        }
      />
      <DataTable
        columns={columns}
        data={payments.isError ? [] : rows}
        loading={payments.isLoading}
        empty={empty}
        enableSorting={false}
        caption="Payments"
        getRowId={(payment) => payment._id}
        className={cn(
          "transition-opacity duration-fast",
          payments.isFetching && !payments.isLoading && "opacity-70"
        )}
        footer={
          payments.data && payments.data.total > 0 ? (
            <Pagination
              page={page}
              totalPages={lastPage}
              onPageChange={setPage}
              totalItems={payments.data.total}
              pageSize={PAGE_SIZE}
              noun="payments"
              disabled={payments.isFetching}
            />
          ) : undefined
        }
      />

      {reversing && (
        <ConfirmDialog
          title="Reverse this payment?"
          description={`${formatINR(reversing.amount)} on ${
            reversing.invoiceNumber || "the invoice"
          } (${formatDate(
            reversing.paidOn || reversing.paidAt
          )}) is taken back off the invoice, so its balance goes up again. Use this for bounced cheques or payments recorded by mistake.`}
          confirmLabel="Reverse payment"
          tone="danger"
          requireReason
          onConfirm={(reason) => actions.reverse.mutateAsync({ paymentId: reversing._id, reason })}
          onClose={() => setReversing(null)}
        />
      )}
    </section>
  );
};

export default PaymentsTab;
